import { ChevronLeft, ExternalLink } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { LibraryBook, CEFRLevel } from '../../../types';
import { getLanguageName } from '../../../lib/constants/languages';

interface RecommendationsStepProps {
  recommendations: LibraryBook[];
  onComplete: () => void;
  onBack: () => void;
  targetLanguage: string;
  cefrLevel: CEFRLevel;
  fallbackUsed?: boolean;
  requestedLanguage?: string;
}

export function RecommendationsStep({
  recommendations,
  onComplete,
  onBack,
  targetLanguage,
  cefrLevel,
  fallbackUsed = false,
  requestedLanguage = '',
}: RecommendationsStepProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const displayLanguage = getLanguageName(targetLanguage);
  const requestedLanguageName = requestedLanguage ? getLanguageName(requestedLanguage) : '';

  const handleStartReading = () => {
    // Build URL params with language and level filters
    const params = new URLSearchParams();
    if (targetLanguage) params.set('language', targetLanguage);
    if (cefrLevel) params.set('grade', cefrLevel);
    
    // Complete onboarding and navigate to library with filters
    onComplete();
    navigate(`/library?${params.toString()}`);
  };
  
  return (
    <div className="recommendations-step">
      <div className="step-header mb-6 text-center">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          {fallbackUsed ? t('onboarding.recommendations.titleFallback') : t('onboarding.recommendations.title')}
        </h3>
        {fallbackUsed ? (
          <div className="fallback-message bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 text-left">
            <p className="text-amber-900 text-sm">
              {t('onboarding.recommendations.fallbackMessage', { language: requestedLanguageName })}
            </p>
            <p className="text-amber-800 text-sm mt-2">
              {t('onboarding.recommendations.fallbackSuggestion')}
            </p>
          </div>
        ) : (
          <p className="text-gray-600">
            {t('onboarding.recommendations.subtitle', { language: displayLanguage, level: cefrLevel })}
          </p>
        )}
      </div>

      {recommendations.length > 0 ? (
        <>
          <div className="recommendations-list space-y-4 mb-6">
            {recommendations.map(book => (
              <Link
                key={book.id}
                to={`/library/books/${book.id}`}
                onClick={onComplete}
                className="book-recommendation flex gap-4 rounded-lg border border-gray-200 p-4 hover:border-primary/50 hover:bg-gray-50 transition-all group"
              >
                {book.cover_url && (
                  <img
                    src={book.cover_url}
                    alt={book.title_international}
                    className="book-cover h-24 w-16 rounded object-cover flex-shrink-0"
                  />
                )}
                <div className="book-info flex-1 min-w-0">
                  <h4 className="text-base font-semibold text-gray-900 mb-1 truncate group-hover:text-primary transition-colors">
                    {book.title_international}
                  </h4>
                  <p className="text-sm text-gray-600 mb-2">
                    {book.author_international}
                  </p>
                  {book.genre && (
                    <span className="inline-block rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-600">
                      {book.genre}
                    </span>
                  )}
                </div>
                <ExternalLink className="h-5 w-5 text-gray-400 group-hover:text-primary transition-colors flex-shrink-0" />
              </Link>
            ))}
          </div>

          <div className="cta-section text-center bg-gray-50 rounded-lg p-6">
            <p className="text-gray-700 mb-4">
              {t('onboarding.recommendations.ctaDescription')}
            </p>
            <button
              onClick={handleStartReading}
              className="complete-btn rounded-lg bg-primary px-8 py-3 text-white font-semibold hover:bg-primary/90 transition-colors"
            >
              {t('onboarding.recommendations.startReading')}
            </button>
          </div>
        </>
      ) : (
        <div className="no-recommendations text-center py-12">
          <p className="text-gray-600 mb-4">
            {t('onboarding.recommendations.loading')}
          </p>
          <div className="loading-spinner mx-auto h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-primary"></div>
        </div>
      )}

      {/* Navigation */}
      <div className="navigation-buttons mt-8 flex justify-between">
        <button
          onClick={onBack}
          className="back-btn flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          {t('common.back')}
        </button>
      </div>
    </div>
  );
}

